import './styles.scss';

import React, { useMemo } from 'react';

import ClientsTableHeader from '@/features/Clients/components/ClientsTable/ClientsTableHeader';
import ClientsTableRow from '@/features/Clients/components/ClientsTable/ClientsTableRow';
import { Provider } from '@/models/gen/graphql';
import Table from '@/common/Table';
import { getProperty } from '@/utils';
import useClientsTableState from '@/features/Clients/components/ClientsTable/hook';

export type ClientsTableProps = {
  data: Provider[];
  selected: string[];
  onSelect: (id: string | string[]) => void;
  onEdit: () => void;
  fetchMore?: () => void;
};

/**
 * Clients feature table component.
 * Wraps `Table` with a column header and row renderer specific to the client entity.
 * Sorting is handled internally via Zustand state; data and selection are received as props.
 */
const ClientsTable = ({ data, selected, onSelect, onEdit, fetchMore }: ClientsTableProps): React.JSX.Element => {
  const [{ sortBy, sortDirection }] = useClientsTableState(({ state }) => [
    { sortBy: state.sorting.column, sortDirection: state.sorting.direction },
  ]);

  const sortedData = useMemo((): Provider[] => {
    if (!sortBy || !sortDirection) return data;
    return [...data].sort((a, b): number => {
      const aValue = getProperty(sortBy, a) as string | number;
      const bValue = getProperty(sortBy, b) as string | number;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const strA = String(aValue ?? '').toLowerCase();
      const strB = String(bValue ?? '').toLowerCase();
      return sortDirection === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
  }, [data, sortBy, sortDirection]);

  const Header = useMemo(
    (): React.JSX.Element => <ClientsTableHeader data={data} selected={selected} onSelect={onSelect} />,
    [data, selected, onSelect]
  );

  return (
    <Table className="ClientsTable" header={Header} showControls onEnd={fetchMore}>
      {sortedData.map(
        (row: Provider): React.JSX.Element => (
          <ClientsTableRow
            key={row.id}
            data={row}
            isSelected={selected.includes(row.id)}
            onSelect={onSelect as (id: string) => void}
            onDoubleClick={(): void => {
              onSelect([row.id]);
              onEdit();
            }}
          />
        )
      )}
    </Table>
  );
};

export default React.memo(ClientsTable);
